import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { GlobalHeader, GlobalFooter, logger, setGlobals, globals, useAdobeBasketDetails } from 'common-ui';
import { initContentfulAPI } from 'common-api/contentful';
import type {
  SiteEntryType,
  ContentfulApiInitConfig,
  HeaderFooterWrapperProps,
  FlagsState,
  GlobalSettings,
} from 'tsconfig/types';
import { useUIConfigStore, useCartStore } from 'common-state';
import { selectCartData } from 'common-state/src/stores/cartStore';

// Mirrors GlobalLayout: expose Contentful feature flags on globals.releaseFlags so
// shared components (e.g. DwChat) can read them in embedded apps.
const setReleaseFlagsFromSiteConfig = (siteConfig?: SiteEntryType): void => {
  const flagsArray = (siteConfig?.fields?.featureFlag as unknown as FlagsState)?.flags;
  if (!flagsArray) {
    return;
  }
  const releaseFlags: { [key: string]: boolean } = {};
  for (const element of flagsArray) {
    releaseFlags[element.key] = element.value;
  }
  // Merge with any flags already on globals so we don't wipe existing entries.
  setGlobals({...globals, releaseFlags: { ...globals.releaseFlags, ...releaseFlags } } as unknown as GlobalSettings);
};

function HeaderFooterWrapper({ config }: HeaderFooterWrapperProps): JSX.Element {
  const [cmsSiteConfig, setCmsSiteConfig] = useState<SiteEntryType>();
  const setMiniCartFlagState = useUIConfigStore(state => state.setMiniCartEnabled);
  const setStickHeaderFlagState = useUIConfigStore(state => state.setStickyHeaderEnabled);
  const { cartItems } = useCartStore(selectCartData) ?? {};
  const adobeBasketDetails = useAdobeBasketDetails();

  const fetchCMSContent = async function (cmsConfig: ContentfulApiInitConfig) {
    const contentfulApi = initContentfulAPI(cmsConfig);
    return contentfulApi.fetchSiteConfig();
  };

  useEffect(() => {
    if (config?.cmsConfig) {
      fetchCMSContent(config.cmsConfig)
        .then(res => {
          setCmsSiteConfig(res);
          setReleaseFlagsFromSiteConfig(res);
          setMiniCartFlagState(res?.fields?.miniCartEnabled || false);
          setStickHeaderFlagState(res?.fields?.stickyHeader || false);
        })
        .catch(() => {
          logger.warn('Invalid cms config');
        });
    }
  }, [config?.cmsConfig, setMiniCartFlagState, setStickHeaderFlagState]);

  // For Adobe Data layer: basketDetails
  useEffect(() => {
    adobeBasketDetails();
  }, [cartItems]);

  //Note: We want to embed the global header AND footer in each page but from a single app. We use createPortal
  const footerRoot = useRef(document.createElement('div'));
  document.getElementById('newFooterReactApp')?.appendChild(footerRoot.current);

  useEffect(() => {
    const navigation: HTMLElement | null = document.querySelector('.stickyMainNavBar-container');
    const mainHeader: HTMLElement | null = document.querySelector('#mainHeaderNavBar');
    const headerBelowSection: HTMLElement | null = document.querySelector('.new-vine-section');
    const handleScroll = () => {
      if (mainHeader && window.scrollY >= mainHeader?.offsetHeight) {
        if (cmsSiteConfig?.fields?.stickyHeader) {
          navigation?.classList?.toggle('sticky-header', true);
          if (headerBelowSection) {
            headerBelowSection.style.marginTop = `${navigation?.offsetHeight}px`;
          }
        }
      } else {
        navigation?.classList?.toggle('sticky-header', false);
        if (headerBelowSection) {
          headerBelowSection.style.marginTop = `0px`;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [cmsSiteConfig]);

  return (
    <>
      {config?.isHeaderEnabled && (
        <GlobalHeader enableCurityLogin={config?.enableCurityLogin} cmsSiteConfig={cmsSiteConfig} />
      )}
      {config?.isFooterEnabled && createPortal(<GlobalFooter cmsSiteConfig={cmsSiteConfig} />, footerRoot.current)}
    </>
  );
}
export default HeaderFooterWrapper;
